import React, { useEffect, useMemo, useState } from 'react';
import { KatButton } from '@amzn/katal-react';
import { AiDiagnosticQuestionResponse, DiagnosticAiService } from '../services/Ai/DiagnosticAiService';
import './DiagnosticAiQuestion.scss';

interface DiagnosticAiQuestionProps {
    stage: string;
    realm: string;
    startDateTime: Date;
    endDateTime: Date;
    selectedClients: string[];
    selectedProcessors: string[];
}

const MAX_HISTORY_DAYS = 30;

const DiagnosticAiQuestion: React.FC<DiagnosticAiQuestionProps> = ({
    stage,
    realm,
    startDateTime,
    endDateTime,
    selectedClients,
    selectedProcessors,
}) => {
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [response, setResponse] = useState<AiDiagnosticQuestionResponse | null>(null);

    const requestScopeKey = useMemo(() => JSON.stringify({
        stage,
        realm,
        start: startDateTime.toISOString(),
        end: endDateTime.toISOString(),
        selectedClients,
        selectedProcessors,
    }), [stage, realm, startDateTime, endDateTime, selectedClients, selectedProcessors]);

    const days = useMemo(() => {
        const ms = Math.max(endDateTime.getTime() - startDateTime.getTime(), 24 * 60 * 60 * 1000);
        const requestedDays = Math.ceil(ms / (24 * 60 * 60 * 1000));
        return Math.max(1, Math.min(MAX_HISTORY_DAYS, requestedDays));
    }, [startDateTime, endDateTime]);

    useEffect(() => {
        setError(null);
        setResponse(null);
    }, [requestScopeKey]);

    const handleAsk = async () => {
        if (!question.trim()) {
            setError('Enter a question to ask Sherlock AI.');
            return;
        }

        setLoading(true);
        setError(null);
        setResponse(null);

        try {
            const aiResponse = await DiagnosticAiService.askQuestion(stage, realm, {
                question: question.trim(),
                days,
                clientIds: sanitizeSelections(selectedClients, 'AllClients'),
                processors: sanitizeSelections(selectedProcessors, 'AllProcessors'),
                windowStart: startDateTime.toISOString(),
                windowEnd: endDateTime.toISOString(),
            });
            setResponse(normalizeDiagnosticAiResponse(aiResponse));
        } catch (err) {
            setResponse(null);
            setError(formatDiagnosticAiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="diagnostic-ai-question">
            <div className="diagnostic-ai-question__header">
                <div className="diagnostic-ai-question__eyebrow">Sherlock AI</div>
                <h3>Diagnostic Intelligence</h3>
            </div>

            <div className="diagnostic-ai-question__composer">
                <div className="diagnostic-ai-question__label-row">
                    <span className="diagnostic-ai-question__label">Question</span>
                    <span className="diagnostic-ai-question__scope">Window: last {days} day{days === 1 ? '' : 's'}</span>
                </div>

                <textarea
                    className="diagnostic-ai-question__input"
                    value={question}
                    onChange={(event) => {
                        setQuestion(event.target.value);
                        setResponse(null);
                        setError(null);
                    }}
                    placeholder="Which processor has the highest stuck-refund count, or what is the total stuck refund amount?"
                    rows={3}
                />

                <div className="diagnostic-ai-question__actions">
                    <KatButton
                        variant="primary"
                        label={loading ? 'Asking Sherlock AI...' : 'Ask Sherlock AI'}
                        onClick={handleAsk}
                        disabled={loading}
                    />
                </div>
            </div>

            {error && <div className="diagnostic-ai-question__error">{error}</div>}

            {response && (
                <div className="diagnostic-ai-question__response">
                    <div className="diagnostic-ai-question__response-header">
                        <span>AI Analysis</span>
                        {response.generatedAt && <span>{response.generatedAt}</span>}
                    </div>

                    <div className="diagnostic-ai-question__answer">{response.answer}</div>

                    {response.topProcessors?.length > 0 && (
                        <div className="diagnostic-ai-question__processors">
                            <h4>Top Processor Changes</h4>
                            <ul>
                                {response.topProcessors.map((processor) => (
                                    <li key={processor.processor}>
                                        {processor.processor}: {processor.firstCount} to {processor.lastCount}
                                        {' '}({formatChange(processor.absoluteChange)})
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {response.caveats?.length > 0 && (
                        <div className="diagnostic-ai-question__caveats">
                            {response.caveats.map((caveat) => (
                                <div key={caveat}>{caveat}</div>
                            ))}
                        </div>
                    )}

                    {(response.windowStart || response.windowEnd || response.generatedAt) && (
                        <div className="diagnostic-ai-question__meta">
                            {response.windowStart && response.windowEnd && (
                                <span>Window: {response.windowStart} to {response.windowEnd}</span>
                            )}
                            {typeof response.snapshotsAnalyzed === 'number' && (
                                <span>Snapshots: {response.snapshotsAnalyzed}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

function sanitizeSelections(values: string[], allMarker: string): string[] | undefined {
    const filtered = values.filter((value) => value && value !== allMarker);
    return filtered.length > 0 ? filtered : undefined;
}

function formatChange(change: number): string {
    return change > 0 ? `+${change}` : `${change}`;
}

function normalizeVisibleText(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.replace(/[\u200B-\u200D\u2060\uFEFF]/g, '').trim();
    return normalized.length > 0 ? normalized : null;
}

function unwrapDiagnosticAiPayload(payload: unknown): unknown {
    if (typeof payload === 'string') {
        try {
            return unwrapDiagnosticAiPayload(JSON.parse(payload));
        } catch {
            return payload;
        }
    }

    if (!payload || typeof payload !== 'object') {
        return payload;
    }

    const candidate = payload as Record<string, unknown>;
    if (typeof candidate.answer === 'string'
        || Array.isArray(candidate.caveats)
        || typeof candidate.snapshotsAnalyzed === 'number') {
        return candidate;
    }

    if ('body' in candidate) {
        return unwrapDiagnosticAiPayload(candidate.body);
    }

    if ('payload' in candidate) {
        return unwrapDiagnosticAiPayload(candidate.payload);
    }

    if ('data' in candidate && Object.keys(candidate).length === 1) {
        return unwrapDiagnosticAiPayload(candidate.data);
    }

    return candidate;
}

function normalizeDiagnosticAiResponse(response: unknown): AiDiagnosticQuestionResponse {
    const unwrappedResponse = unwrapDiagnosticAiPayload(response);
    if (!unwrappedResponse || typeof unwrappedResponse !== 'object') {
        return {
            answer: 'Sherlock AI returned an empty answer. Retry the question, narrow the scope, or refresh the page if the issue persists.',
            caveats: ['The backend response was missing the expected Sherlock AI answer payload.'],
            topProcessors: [],
            snapshotsAnalyzed: 0,
            generatedAt: new Date().toISOString(),
        };
    }

    const candidate = unwrappedResponse as Partial<AiDiagnosticQuestionResponse>;
    const answer = normalizeVisibleText(candidate.answer)
        ? normalizeVisibleText(candidate.answer)!
        : 'Sherlock AI returned an empty answer. Retry the question, narrow the scope, or refresh the page if the issue persists.';
    const caveats = Array.isArray(candidate.caveats)
        ? candidate.caveats
            .map((value) => normalizeVisibleText(value))
            .filter((value): value is string => Boolean(value))
        : [];

    if (!normalizeVisibleText(candidate.answer)) {
        caveats.unshift('The backend returned a response without answer text, so Sherlock UI showed this fallback message.');
    }

    return {
        answer,
        caveats,
        topProcessors: Array.isArray(candidate.topProcessors) ? candidate.topProcessors : [],
        windowStart: candidate.windowStart,
        windowEnd: candidate.windowEnd,
        snapshotsAnalyzed: typeof candidate.snapshotsAnalyzed === 'number' ? candidate.snapshotsAnalyzed : 0,
        modelId: candidate.modelId,
        generatedAt: candidate.generatedAt ?? new Date().toISOString(),
    };
}

function formatDiagnosticAiError(error: unknown): string {
    if (!(error instanceof Error)) {
        return 'Failed to ask Sherlock AI';
    }

    const normalizedMessage = error.message.toLowerCase();
    if (normalizedMessage.includes('timed out') || normalizedMessage.includes('timeout')) {
        return 'Sherlock AI took too long to respond. Please retry in a moment or narrow the question scope.';
    }
    if (normalizedMessage.includes('failed to fetch') || normalizedMessage.includes('network error') || normalizedMessage.includes('load failed')) {
        return 'Sherlock API request failed before the backend responded. Refresh the page to renew auth, then retry the question.';
    }

    return error.message;
}

export default DiagnosticAiQuestion;
